import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock, ShieldCheck } from 'lucide-react';
import { SeverityMeter, SIGNAL_DISCLAIMER } from './SeverityMeter';
import { getPatternDefinition } from '../../../data/patternDefinitions';
import type { NormalizedPattern } from '../../../utils/scanReport';
interface DetectedPatternGridProps {
  patterns: NormalizedPattern[];
  /** Full-access reports show the conversation-specific description. */
  isUnlocked: boolean;
}
const SEVERITY_ORDER = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2
} as const;
export function DetectedPatternGrid({
  patterns,
  isUnlocked
}: DetectedPatternGridProps) {
  const reduceMotion = useReducedMotion();
  if (patterns.length === 0) {
    return <section className="flex items-start gap-4 rounded-2xl border border-emerald-400/20 bg-forensic-surface p-6" data-id="element-171" data-mp="1ueeus2w1901a">
        <ShieldCheck className="mt-0.5 shrink-0 text-emerald-400" size={20} aria-hidden="true" data-id="element-172" data-mp="4yffe41tmed7" />
        <div data-id="element-173" data-mp="x5aac2ht3m">
          <h2 className="text-base font-semibold text-white" data-id="element-174" data-mp="16tpthgrs77">
            No significant patterns detected
          </h2>
          <p className="mt-1 text-sm text-forensic-muted" data-id="element-175" data-mp="1csxpax8flnhw">
            The engine didn’t flag recurring manipulation patterns in this
            conversation.
          </p>
        </div>
      </section>;
  }
  // Strongest signals first so the most important finding is read first.
  const sorted = [...patterns].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  const highCount = patterns.filter(p => p.severity === 'HIGH').length;
  return <section aria-labelledby="patterns-heading" data-id="element-176" data-mp="1vli3d8jh1o1n">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-2" data-id="element-177" data-mp="20jtf5otbu8mi">
        <div data-id="element-178" data-mp="24kn0qbjarbc8">
          <h2 id="patterns-heading" className="text-lg font-semibold text-white sm:text-xl" data-id="element-179" data-mp="24jbv066ajtih">
            Detected patterns
          </h2>
          <p className="mt-1 text-xs text-forensic-subtle" data-id="element-180" data-mp="66tuhha3ao8d">{SIGNAL_DISCLAIMER}</p>
        </div>
        <p className="text-sm text-forensic-muted tabular-nums" data-id="element-181" data-mp="a8f0cio7i292">
          {patterns.length} total
          {highCount > 0 && <span className="text-forensic-crimson-soft" data-id="element-182" data-mp="18iewke4b3q7j"> · {highCount} high signal</span>}
        </p>
      </div>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-183" data-mp="1mizfdaa81m11">
        {sorted.map((pattern, idx) => <motion.li key={`${pattern.type}-${idx}`} initial={reduceMotion ? false : {
        opacity: 0,
        y: 8
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.24,
        delay: Math.min(idx * 0.045, 0.27),
        ease: [0.23, 1, 0.32, 1]
      }} className={`flex h-full flex-col rounded-xl border bg-forensic-card p-5 ${pattern.severity === 'HIGH' ? 'border-forensic-crimson/30' : 'border-forensic-line'}`} data-id="element-184" data-mp="1iernx5cdprz4" data-mpi={idx}>
            <SeverityMeter severity={pattern.severity} data-id="element-185" data-mp="2a93fh7o7b3na" />
            <h3 className="mt-3 text-[15px] font-semibold leading-snug text-white" data-id="element-186" data-mp="2tssoxvifb3q2">
              {pattern.type}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-forensic-muted" data-id="element-187" data-mp="2yr2la9fvwad9">
              {isUnlocked && pattern.description ? pattern.description : getPatternDefinition(pattern.type)}
            </p>
            {!isUnlocked && pattern.evidenceLocked && <div className="mt-auto pt-4" data-id="element-188" data-mp="2b215200t7uxf">
                <p className="flex items-center gap-1.5 border-t border-forensic-line pt-4 text-xs text-forensic-subtle" data-id="element-189" data-mp="2b0zs1q08gyd2">
                  <Lock size={12} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-190" data-mp="21556on4mkp6l" />
                  Evidence locked
                </p>
              </div>}
          </motion.li>)}
      </ul>
    </section>;
}